import { supabase } from '../../lib/supabaseClient';

// Types locaux — pas (encore) dans database.types.ts.
export interface CabinetFolder {
  id: string;
  parent_id: string | null;
  name: string;
  emoji: string;
  is_root: boolean;
  depth: number;
  created_by: string | null;
  created_at: string;
}

export interface CabinetFile {
  id: string;
  folder_id: string;
  storage_path: string;
  filename: string;
  mime: string;
  size: number;
  uploader_id: string | null;
  created_at: string;
}

export interface CabinetTag {
  id: string;
  name: string;
  color: string;
  created_by: string | null;
  created_at: string;
}

export interface CabinetFolderMember {
  folder_id: string;
  collaborator_id: string;
  can_view: boolean;
  can_edit: boolean;
  created_at: string;
}

export const CABINET_BUCKET = 'cabinet-files';
export const CABINET_MAX_FILE_SIZE = 50 * 1024 * 1024;

// ─── Folders ────────────────────────────────────────────────────────────

export async function listFolders(): Promise<CabinetFolder[]> {
  const { data, error } = await supabase
    .from('cabinet_folders')
    .select('*')
    .order('is_root', { ascending: false })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as CabinetFolder[];
}

export async function createFolder(input: {
  parentId: string | null;
  name: string;
  emoji?: string;
  isRoot?: boolean;
  createdBy: string | null;
}): Promise<CabinetFolder> {
  const { data, error } = await supabase
    .from('cabinet_folders')
    .insert({
      parent_id: input.parentId,
      name: input.name.trim(),
      emoji: input.emoji ?? 'folder',
      is_root: input.isRoot ?? false,
      created_by: input.createdBy,
    })
    .select()
    .single();
  if (error) throw error;
  return data as CabinetFolder;
}

export async function renameFolder(folderId: string, name: string): Promise<CabinetFolder> {
  const { data, error } = await supabase
    .from('cabinet_folders')
    .update({ name: name.trim() })
    .eq('id', folderId)
    .select()
    .single();
  if (error) throw error;
  return data as CabinetFolder;
}

export async function updateFolderEmoji(folderId: string, emoji: string): Promise<CabinetFolder> {
  const { data, error } = await supabase
    .from('cabinet_folders')
    .update({ emoji })
    .eq('id', folderId)
    .select()
    .single();
  if (error) throw error;
  return data as CabinetFolder;
}

export async function deleteFolder(folderId: string): Promise<void> {
  const { error } = await supabase.from('cabinet_folders').delete().eq('id', folderId);
  if (error) throw error;
}

// ─── Resolve root ───────────────────────────────────────────────────────

/** Remonte la chaîne parent_id jusqu'à trouver le folder racine (is_root=true). */
export function resolveRootFolder(folderId: string, folders: CabinetFolder[]): CabinetFolder | null {
  const byId = new Map(folders.map((f) => [f.id, f]));
  let cur = byId.get(folderId);
  while (cur && !cur.is_root) {
    if (!cur.parent_id) return null;
    cur = byId.get(cur.parent_id);
  }
  return cur ?? null;
}

// ─── Files ──────────────────────────────────────────────────────────────

export async function listFiles(folderId: string): Promise<CabinetFile[]> {
  const { data, error } = await supabase
    .from('cabinet_files')
    .select('*')
    .eq('folder_id', folderId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as CabinetFile[];
}

export async function uploadFile(input: {
  folderId: string;
  rootFolderId: string;
  uploaderId: string | null;
  file: File;
}): Promise<CabinetFile> {
  if (input.file.size > CABINET_MAX_FILE_SIZE) {
    throw new Error('Fichier trop volumineux (max 50 Mo).');
  }
  const fileId = crypto.randomUUID();
  const lastDot = input.file.name.lastIndexOf('.');
  const ext = lastDot > 0 ? input.file.name.slice(lastDot + 1) : '';
  const storagePath = ext
    ? `${input.rootFolderId}/${fileId}.${ext}`
    : `${input.rootFolderId}/${fileId}`;

  const { error: upErr } = await supabase.storage
    .from(CABINET_BUCKET)
    .upload(storagePath, input.file, {
      cacheControl: '3600',
      upsert: false,
      contentType: input.file.type || 'application/octet-stream',
    });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from('cabinet_files')
    .insert({
      id: fileId,
      folder_id: input.folderId,
      storage_path: storagePath,
      filename: input.file.name,
      mime: input.file.type || 'application/octet-stream',
      size: input.file.size,
      uploader_id: input.uploaderId,
    })
    .select()
    .single();

  if (error) {
    await supabase.storage.from(CABINET_BUCKET).remove([storagePath]).catch(() => {});
    throw error;
  }
  return data as CabinetFile;
}

export async function deleteFile(fileId: string): Promise<void> {
  const { data: row, error: getErr } = await supabase
    .from('cabinet_files')
    .select('storage_path')
    .eq('id', fileId)
    .single();
  if (getErr) throw getErr;
  await supabase.storage.from(CABINET_BUCKET).remove([row.storage_path]).catch(() => {});
  const { error } = await supabase.from('cabinet_files').delete().eq('id', fileId);
  if (error) throw error;
}

export async function getDownloadUrl(fileId: string): Promise<string> {
  const { data: row, error: getErr } = await supabase
    .from('cabinet_files')
    .select('storage_path')
    .eq('id', fileId)
    .single();
  if (getErr) throw getErr;
  const { data, error } = await supabase.storage
    .from(CABINET_BUCKET)
    .createSignedUrl(row.storage_path, 60 * 60);
  if (error) throw error;
  return data.signedUrl;
}

export async function renameFile(fileId: string, filename: string): Promise<CabinetFile> {
  const { data, error } = await supabase
    .from('cabinet_files')
    .update({ filename: filename.trim() })
    .eq('id', fileId)
    .select()
    .single();
  if (error) throw error;
  return data as CabinetFile;
}

// ─── Tags ────────────────────────────────────────────────────────────────

export async function listTags(): Promise<CabinetTag[]> {
  const { data, error } = await supabase
    .from('cabinet_tags')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as CabinetTag[];
}

export async function createTag(name: string, color: string, createdBy: string | null): Promise<CabinetTag> {
  const { data, error } = await supabase
    .from('cabinet_tags')
    .insert({ name: name.trim(), color, created_by: createdBy })
    .select()
    .single();
  if (error) throw error;
  return data as CabinetTag;
}

export async function deleteTag(tagId: string): Promise<void> {
  const { error } = await supabase.from('cabinet_tags').delete().eq('id', tagId);
  if (error) throw error;
}

export async function listFileTagIds(fileId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('cabinet_file_tags')
    .select('tag_id')
    .eq('file_id', fileId);
  if (error) throw error;
  return (data ?? []).map((r) => r.tag_id as string);
}

export async function listAllFileTags(): Promise<Array<{ file_id: string; tag_id: string }>> {
  const { data, error } = await supabase.from('cabinet_file_tags').select('file_id, tag_id');
  if (error) throw error;
  return (data ?? []) as Array<{ file_id: string; tag_id: string }>;
}

export async function setFileTags(fileId: string, tagIds: string[]): Promise<void> {
  // Remplace tous les tags du fichier.
  const { error: delErr } = await supabase
    .from('cabinet_file_tags')
    .delete()
    .eq('file_id', fileId);
  if (delErr) throw delErr;
  if (tagIds.length === 0) return;
  const { error } = await supabase
    .from('cabinet_file_tags')
    .insert(tagIds.map((tag_id) => ({ file_id: fileId, tag_id })));
  if (error) throw error;
}

// ─── Members (admin only) ────────────────────────────────────────────────

export async function listRootMembers(rootFolderId: string): Promise<CabinetFolderMember[]> {
  const { data, error } = await supabase
    .from('cabinet_folder_members')
    .select('*')
    .eq('folder_id', rootFolderId);
  if (error) throw error;
  return (data ?? []) as CabinetFolderMember[];
}

export async function upsertMember(input: {
  folderId: string;
  collaboratorId: string;
  canView: boolean;
  canEdit: boolean;
}): Promise<void> {
  const { error } = await supabase
    .from('cabinet_folder_members')
    .upsert(
      {
        folder_id: input.folderId,
        collaborator_id: input.collaboratorId,
        can_view: input.canView,
        can_edit: input.canEdit,
      },
      { onConflict: 'folder_id,collaborator_id' },
    );
  if (error) throw error;
}

export async function removeMember(folderId: string, collaboratorId: string): Promise<void> {
  const { error } = await supabase
    .from('cabinet_folder_members')
    .delete()
    .eq('folder_id', folderId)
    .eq('collaborator_id', collaboratorId);
  if (error) throw error;
}
